import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  ScaffolderFieldExtensions,
  ScaffolderLayouts,
} from '@backstage/plugin-scaffolder-react';
import { RepoNameFromAppFieldExtension } from './scaffolder/RepoNameFromApp';
import { UniqueAppNameFieldExtension } from './scaffolder/UniqueAppName';
import { StarterPickerFieldExtension } from './scaffolder/StarterPicker';
import { GitHubOAuthFieldExtension } from './scaffolder/GitHubOAuthField';
import { CollapsibleSectionLayout } from './scaffolder/CollapsibleSection';
import { DraftCreatePage } from './scaffolder/DraftCreatePage';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  ExpandableNavigation,
  LightBox,
  TextSize,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { catalogColumns } from './components/catalog/catalogColumns';
import { catalogEntityPresentationModule } from './entityPresentation';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import CssBaseline from '@mui/material/CssBaseline';
import { createApp } from '@backstage/frontend-defaults';
import {
  AppRootElementBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
// Native New Frontend System plugin — it has no legacy equivalent, so it could
// not be installed before this migration. Serves /oauth2/authorize/:sessionId,
// the OIDC consent screen the auth-backend's OAuth2 flow redirects to, which is
// the frontend half of the Client ID Metadata Documents prerequisite.
import authPlugin from '@backstage/plugin-auth';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@mui/icons-material/WbSunny';
import DarkIcon from '@mui/icons-material/Brightness2';
import { velocityLightTheme, velocityDarkTheme } from './theme';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { GraphiQLPage } from '@backstage-community/plugin-graphiql';
import { GcpNetworkDiagramPage } from '@internal/plugin-gcp-network-diagram';

import {
  configApiRef,
  oktaAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
// Homepage
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';

/**
 * Everything the legacy `createApp({ ... })` call used to take, converted into
 * a New Frontend System module. APIs, themes and the custom SignInPage keep
 * their legacy shape — `convertLegacyAppOptions` wraps each of them in the
 * right blueprint (ApiBlueprint / ThemeBlueprint / SignInPageBlueprint).
 */
const legacyAppOptions = convertLegacyAppOptions({
  apis,
  themes: [
    {
      id: 'velocity-light',
      title: 'Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={velocityLightTheme} children={children} />
      ),
    },
    {
      id: 'velocity-dark',
      title: 'Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={velocityDarkTheme} children={children} />
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const config = useApi(configApiRef);
      const baseUrl = config.getString('app.baseUrl');
      const isLocal =
        baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
      return (
        <SignInPage
          {...props}
          auto
          providers={
            isLocal
              ? ['guest']
              : [
                  {
                    id: 'okta-auth-provider',
                    title: 'Okta',
                    message: 'Sign in using Okta',
                    apiRef: oktaAuthApiRef,
                  },
                ]
          }
        />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route
      path="/catalog"
      element={<CatalogIndexPage columns={catalogColumns} />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <ExpandableNavigation />
        <LightBox />
        <TextSize />
        <Mermaid
          enableZoom
          config={{ themeVariables: { lineColor: '#8f8f8f' } }}
        />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          templateFilter={entity =>
            !(entity.metadata.tags?.includes('day-2') ?? false)
          }
        />
      }
    >
      <ScaffolderFieldExtensions>
        <RepoNameFromAppFieldExtension />
        <UniqueAppNameFieldExtension />
        <StarterPickerFieldExtension />
        <GitHubOAuthFieldExtension />
      </ScaffolderFieldExtensions>
      <ScaffolderLayouts>
        <CollapsibleSectionLayout />
      </ScaffolderLayouts>
    </Route>
    {/*
      Renders any template prefilled from a draft in the URL fragment, for the
      "an agent prepared this, a human presses Create" handoff. Outside
      /create on purpose: that route's children are its extension registry, and
      this page needs the same extensions passed as props instead — see
      scaffolder/extensions.ts, which both take their list from.
    */}
    <Route path="/create/draft" element={<DraftCreatePage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/graphiql" element={<GraphiQLPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/gcp-network-diagram" element={<GcpNetworkDiagramPage />} />
  </FlatRoutes>
);

/**
 * The legacy app element tree. `convertLegacyAppRoot` walks it and emits one
 * New Frontend System page extension per `<Route>`, plus an `app/root` layout
 * override that keeps our custom `<Root>` sidebar.
 *
 * Note what it does *not* carry over: only the `<AppRouter>` subtree is read,
 * so the siblings below are dropped. `<AlertDisplay/>` and
 * `<OAuthRequestDialog/>` are fine — `@backstage/plugin-app` renders its own.
 * `<CssBaseline/>` and `<SignalsDisplay/>` are not, so both are re-attached as
 * app root elements in `appRootElementsModule` below.
 */
const legacyAppRoot = (
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>
);

const appRootElementsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    // Nothing in the NFS app root emits MUI's baseline stylesheet, and losing
    // it is not subtle: `body` falls back to the browser default (Times New
    // Roman for anything not inside a <Typography>), and without the global
    // `box-sizing: border-box` the SidebarPage content — width:100% plus a
    // padding-left of the closed sidebar's width — overflows the viewport by
    // exactly that width, so every page gets a horizontal scrollbar.
    AppRootElementBlueprint.make({
      name: 'css-baseline',
      params: { element: <CssBaseline /> },
    }),
    // @backstage/plugin-signals/alpha only provides the signals API — the
    // display component has no extension of its own, so we attach it here.
    AppRootElementBlueprint.make({
      name: 'signals-display',
      params: { element: <SignalsDisplay /> },
    }),
  ],
});

const app = createApp({
  features: [
    ...convertLegacyAppRoot(legacyAppRoot),
    legacyAppOptions,
    appRootElementsModule,
    catalogEntityPresentationModule,
    authPlugin,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const appRoot = app.createRoot();

export default function App() {
  return appRoot;
}
